import React, { FC, useCallback, useContext, useEffect, useState } from 'react';

import { Table, Typography, Card as CardAnt } from 'antd';

import {
  ColSize,
  ContentSection,
  Form,
  InputForm,
  RowSize,
  SelectForm,
} from '../../../../../../../../../../../../../shared/components';

import { GenericDataFormContext, QuotationInfoContext } from '../../../../../../../../../shared/contexts';
import { StepFiveFormContext } from '../../../../contexts/StepFiveForm';
import { StepThreeFormContext } from '../../../../../../../StepThree/contexts/StepThreeForm';

import { FireQuotationComponent } from '../../../../../../../../../../../../../shared/interfaces/services/fire-quotation-service';

import {
  formattedPrice,
  ValidateForm,
} from '../../../../../../../../../../../../../shared/utils';
import { COLORS } from '../../../../../../../../../../../../../config/themes/colors';

import './index.css';
import { TransferRightsForm } from '../../../TransferRightsForm';

interface Coverage {
  codigo: string;
  description: string;
  familyCode?: string;
  familyDescription?: string;
  key: string;
  riskCode: string;
  sumInsured: string;
  typeCoverage?: string;
}

interface CoverageGroup {
  [familyCode: string]: Coverage[];
}

interface FormCoveragesProps {
  fireComponent: FireQuotationComponent;
}

const MOCK_TABLE_COLUMNS_HEADER = [
  {
    title: 'Descripción de cobertura',
    dataIndex: 'description',
    width: '50%',
  },
  {
    title: 'Suma asegurada',
    dataIndex: 'sumInsured',
    width: '50%',
  },
];

export const FormCoverages: FC<FormCoveragesProps> = ({ fireComponent }) => {
  const validateForm = new ValidateForm();

  const { propertyDataForm } = useContext(StepFiveFormContext);
  const { provinceList, allLocalitiesPerProvince, allCoveragesPerPlan } =
    useContext(GenericDataFormContext);
  const { stepThreeForm } = useContext(StepThreeFormContext);

  const [coveragesListInfo, setCoveragesListInfo] = useState<CoverageGroup[]>(
    []
  );

  const planSelected = stepThreeForm?.getFieldValue(
    `plan-tab-${fireComponent.nroComponente}`
  );

  const provinceValueByTab = stepThreeForm?.getFieldValue(
    `province-tab-${fireComponent.nroComponente}`
  );
  const locationValueByTab = stepThreeForm?.getFieldValue(
    `location-tab-${fireComponent.nroComponente}`
  );
  const localitiesPerProvince = allLocalitiesPerProvince.find(
    (loc) => String(loc.provinceCode) === String(provinceValueByTab)
  )?.localitiesOptions;

  const setValuesForm = () => {
    propertyDataForm.setFieldValue(
      `propertyDataProvince${fireComponent.nroComponente}`,
      provinceValueByTab
    );
    propertyDataForm.setFieldValue(
      `propertyDataLocality${fireComponent.nroComponente}`,
      locationValueByTab
    );
  };

  const propertyDataAddressValueByTab = stepThreeForm?.getFieldValue(`propertyDataAddress${fireComponent.nroComponente}`);

  const setCompleteCoveragesListInfo = useCallback(() => {
    const matchingCoveragesByTab = allCoveragesPerPlan?.find(
      (el) => String(el.plan as string) === String(planSelected)
    );

    if (matchingCoveragesByTab) {
      const filteredCoverages = matchingCoveragesByTab.coverages.filter(
        (planCoverage) =>
          fireComponent.cobertura.some(
            (cov) => cov.cobertura === planCoverage.codigo
          )
      );

      const coveragesListInfo = filteredCoverages.map((planCoverage) => {
        const matchedCoverage = fireComponent.cobertura.find(
          (cov) => cov.cobertura === planCoverage.codigo
        );

        let familyDescription;

        if (planCoverage?.codigoFamilia) {
          familyDescription = planCoverage.descripcionFamilia;
        } else if (planCoverage.tipoDeCobertura === 'O') {
          familyDescription = 'ADICIONAL';
        } else {
          familyDescription = 'OTRAS';
        }

        return {
          codigo: planCoverage.codigo,
          description: planCoverage.descripcionLarga,
          familyCode:
            planCoverage?.codigoFamilia ??
            (planCoverage.tipoDeCobertura === 'O'
              ? 'additional'
              : 'noFamilyCode'),
          familyDescription,
          key: planCoverage.codigo,
          riskCode: planCoverage.codigoRiesgo,
          sumInsured: `$ ${formattedPrice(
            `${matchedCoverage ? matchedCoverage.sumaAsegurada : 0}`
          )}`,
          typeCoverage: planCoverage?.tipoDeCobertura,
        };
      });

      const groupedData = coveragesListInfo.reduce<{
        [key: string]: Coverage[];
      }>((acc, item) => {
        let codeFamilyData: string;

        codeFamilyData = item.familyCode;

        if (!acc[codeFamilyData]) acc[codeFamilyData] = [];

        acc[codeFamilyData].push(item);
        return acc;
      }, {});

      const desiredOrder = ['noFamilyCode', 'additional'];
      const orderedGroupedData: { [key: string]: Coverage[] } = {};

      desiredOrder.forEach((key) => {
        if (groupedData[key]) orderedGroupedData[key] = groupedData[key];
      });

      Object.keys(groupedData)
        .filter((key) => !desiredOrder.includes(key))
        .forEach((key) => {
          orderedGroupedData[key] = groupedData[key];
        });

      setCoveragesListInfo([orderedGroupedData]);

      return;
    } else {
      setCoveragesListInfo([]);
    }
  }, [
    allCoveragesPerPlan,
    planSelected,
    fireComponent.cobertura,
    fireComponent.nroComponente,
    stepThreeForm,
  ]);

  useEffect(() => {
    setCompleteCoveragesListInfo();
  }, [setCompleteCoveragesListInfo]);

  useEffect(() => {
    setValuesForm();
  }, [fireComponent]);

  const { renewalQuotationState } = useContext(QuotationInfoContext);
  const isRenewal = !!renewalQuotationState;

  return (
    <CardAnt>
      <ContentSection level='h3' title='Datos del bien'>
        <Form
          formProps={{
            form: propertyDataForm,
            name: `activity-form-${fireComponent.nroComponente}`,
          }}
        >
          <RowSize>
            <ColSize size={25}>
              <InputForm
                formProps={{
                  label: 'Domicilio:',
                  name: `propertyDataAddress${fireComponent.nroComponente}`,
                  rules: validateForm.generateRules({
                    label: 'domicilio',
                    rules: ['required'],
                  }),
                  initialValue: isRenewal ? (propertyDataAddressValueByTab ?? '') : '',
                }}
                inputProps={{ placeholder: 'Senillosa 342 1B', disabled: !!propertyDataAddressValueByTab && isRenewal, }}
              />
            </ColSize>

            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Provincia:',
                  name: `propertyDataProvince${fireComponent.nroComponente}`,
                }}
                selectProps={{
                  disabled: true,
                  loading: provinceList?.length === 0,
                  options: provinceList,
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Localidad:',
                  name: `propertyDataLocality${fireComponent.nroComponente}`,
                }}
                selectProps={{
                  disabled: true,
                  options: localitiesPerProvince,
                }}
              />
            </ColSize>
          </RowSize>
        </Form>
      </ContentSection>

      <ContentSection level='h3' title='Cobertura' hasCard={false}>
        {coveragesListInfo?.map((coverageGroup: any) => {
          const familyCodes = Object.keys(coverageGroup);

          return familyCodes.map((familyCode, index) => {
            const coverages = coverageGroup[familyCode];

            return (
              <React.Fragment key={`${familyCode}-${index}`}>
                <ColSize size={100}>
                  <Typography.Title
                    level={5}
                    style={{
                      margin: 0,
                      marginBottom: 10,
                      color: COLORS.secondary[100],
                    }}
                  >
                    {coverages[0]?.familyDescription}
                  </Typography.Title>
                </ColSize>
                <Table
                  bordered
                  className='person-table'
                  columns={MOCK_TABLE_COLUMNS_HEADER}
                  dataSource={coverages}
                  pagination={false}
                  scroll={{ x: 'auto' }}
                />
              </React.Fragment>
            );
          });
        })}
      </ContentSection>
      <TransferRightsForm fireComponent={fireComponent} />
    </CardAnt>
  );
};
